import React, { useEffect, useState } from "react"

import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

import EditorFooter from "./shared/EditorFooter"

const DEFAULT_SETTINGS = {
  showLogos: true,
  showDescriptions: true,
  credentials: [],
  showBackgroundColor: false,
  backgroundColor: "#ffffff"
}

const buildInitialFormData = (section) => {
  const showBackgroundColor = section?.settings?.showBackgroundColor || false
  const backgroundColor =
    section?.settings?.backgroundColor || (showBackgroundColor ? "theme" : DEFAULT_SETTINGS.backgroundColor)

  return {
    headline: section?.headline || "Professional Credentials",
    subheadline: section?.subheadline || "Qualified and experienced in our field",
    body: section?.body || "",
    settings: {
      ...DEFAULT_SETTINGS,
      ...section?.settings,
      showBackgroundColor,
      backgroundColor
    }
  }
}

const CredentialsEditor = ({ section, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState(() => buildInitialFormData(section))
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    setFormData(buildInitialFormData(section))
  }, [section])

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const updatedSettings = { ...prev.settings, [key]: value }
      const updatedFormData = { ...prev, settings: updatedSettings }

      if (section?.id && ["showBackgroundColor", "backgroundColor"].includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...updatedFormData,
          settings: {
            ...section.settings,
            ...updatedSettings
          }
        })
      }

      return updatedFormData
    })
  }

  const handleSubmit = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    onSave({
      layout: "credentials",
      ...formData
    })
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  return (
    <div
      className="relative box-border flex size-full flex-col content-stretch items-center justify-start bg-white shadow-[0px_8px_24px_0px_rgba(33,37,41,0.12)]"
      data-name="Credentials"
      data-node-id="1025:23393">
      {/* Form Content */}
      <div
        className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pb-[120px] pt-6"
        data-name="form"
        data-node-id="1025:23395">
        <div
          className="relative flex w-[432px] shrink-0 flex-col content-stretch items-start justify-start gap-4"
          data-name="Builder / Keywords"
          data-node-id="1025:23396">
          <div
            className="relative flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 leading-[0] text-[#0b3954]"
            id="node-I1025_23396-984_20635">
            <div
              className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold"
              id="node-I1025_23396-984_20636">
              <p className="leading-[24px]">Credentials</p>
            </div>
            <div
              className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal"
              id="node-I1025_23396-984_20637">
              <p className="leading-[20px]">
                <span className>{`Manage your credentials from the `}</span>
                <span className="text-[#0d9da4]">{`My Practice > Credentials`}</span>
                <span className>{` section.`}</span>
              </p>
            </div>
          </div>

          <div className="w-full rounded-lg border border-gray-200 p-4">
            <BackgroundColorToggle
              formData={formData}
              onSettingsChange={handleSettingsChange}
              sectionType="credentials"
            />
          </div>
        </div>
      </div>

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </div>
  )
}

export default CredentialsEditor
